import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { prop } from '@ipfabric/utils/fp/prop';
import type { GlobalStateMergedState } from 'components/GlobalState/GlobalStateModel';
import {
  type RoutesByPath,
  selectAllRoutesByPath,
} from 'components/GlobalState/selectors/selectAllRoutesByPath';
import { selectLicenseFeatureAvailability } from 'components/GlobalState/selectors/selectLicenseFeatureAvailability';
import {
  type MenuTreeNode,
  type UrlListItem,
  createMenuTree,
  isGraphLocationDescriptor,
  isPageItem,
} from 'components/Sidebar/menuTree';
import type { RoutesTreeItem } from 'containers/App/Routes/RoutesTreeItem';
import { createSelector } from 'reselect';
import type { PermissionScope } from 'utils/hooks/usePermissionScope';
import type { LicenseFeatureAvailability } from 'utils/license/licenseFeatures';
import { CLOUD_LICENSING_PAGE_PATH } from 'utils/pageGating/cloudLicensingPageGating';
import {
  evaluatePageAccess,
  pageAccessRuleFromOasNode,
} from 'utils/pageGating/pageGating';
import {
  type MeetsApiScopeFunction,
  selectMeetsApiScope,
} from 'utils/rbac/selectMeetsApiScope';
import { createScopedSelector } from 'utils/react/scopedSelector';

// menu tree without access restrictions
const selectWholeMenuTree: (s: GlobalStateMergedState) => MenuTreeNode[] =
  createScopedSelector(() =>
    createSelector(
      prop('msg'),
      prop('oas'),
      prop('snapshotId'),
      selectAllRoutesByPath,
      createMenuTree,
    ),
  );

const filterMenuTreeByPermissionRestrictions = (
  menuTreeNodes: MenuTreeNode[],
  meetsApiScope: MeetsApiScopeFunction,
  routesByPath: RoutesByPath,
  legacyScopesOfCurrentUser: PermissionScope[],
  admin?: boolean,
) => {
  if (admin) {
    // just a shortcut to save all the iteration; admin has all legacy scopes and is checked in meetsApiScope anyway.
    return menuTreeNodes;
  }

  const isScopeAllowed = (s: PermissionScope) =>
    legacyScopesOfCurrentUser.indexOf(s) !== -1;

  const isRestrictedRoute = (restrictionRoute: RoutesTreeItem): boolean => {
    if (
      restrictionRoute.requiredApiScope &&
      !meetsApiScope(restrictionRoute.requiredApiScope)
    ) {
      return true;
    }
    if (!(restrictionRoute.requiredPermissions ?? []).every(isScopeAllowed)) {
      return true;
    }
    return false;
  };

  const isRestrictedByUrlList = (
    urlList: UrlListItem[],
    menuTreeNode: MenuTreeNode,
  ): boolean => {
    const getRestrictionUrl = (
      url: UrlListItem,
    ): RoutesTreeItem | undefined => {
      if (typeof url === 'string') {
        return routesByPath[url];
      }
      if (isPageItem(url)) {
        return routesByPath[url.url];
      }
      if (isGraphLocationDescriptor(url)) {
        return routesByPath[url.to];
      }
      return undefined;
    };

    // Fixed noUncheckedIndexedAccess NIM-19408
    const restrictionUrl = getRestrictionUrl(urlList[0]!);
    return restrictionUrl
      ? isRestrictedMenuTreeNodeByRoute(restrictionUrl, menuTreeNode)
      : false;
  };

  const isRestrictedMenuTreeNodeByRoute = (
    restrictionRoute: RoutesTreeItem,
    item: MenuTreeNode,
  ) => {
    if (restrictionRoute) {
      if (isRestrictedRoute(restrictionRoute)) {
        return true;
      }
    } else {
      stumble(
        `Menu item ${item.label} is restricted by non-existent route ${item.restrictByRoute}`,
      );
      // still include menu item with invalid definition
    }
    return false;
  };

  const isRestrictedMenuTreeNodeByRoutesTree = (menuTreeNode: MenuTreeNode) => {
    let routePath = menuTreeNode.restrictByRoute ?? null;
    let nesting = 0;
    while (routePath && nesting < 100) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const restrictionRoute = routesByPath[routePath]!;
      if (isRestrictedMenuTreeNodeByRoute(restrictionRoute, menuTreeNode)) {
        return true;
      }
      routePath = restrictionRoute.parent;
      nesting += 1;
    }
    if (nesting === 100) {
      stumble(`Probable route parent cycle detected on route ${routePath}`);
    }
    return false;
  };

  const isRestrictedMenuTreeNode = (menuTreeNode: MenuTreeNode): boolean => {
    return (
      isRestrictedMenuTreeNodeByRoutesTree(menuTreeNode) ||
      (!!menuTreeNode.urlList &&
        isRestrictedByUrlList(menuTreeNode.urlList, menuTreeNode)) ||
      !(menuTreeNode.permissions ?? []).every(isScopeAllowed) ||
      (!!menuTreeNode.apiScopeRequirement &&
        !meetsApiScope(menuTreeNode.apiScopeRequirement))
    );
  };

  return menuTreeNodes.reduce<MenuTreeNode[]>((acc, item) => {
    let newChildren: MenuTreeNode[] | undefined;
    if (item.children) {
      newChildren = filterMenuTreeByPermissionRestrictions(
        item.children,
        meetsApiScope,
        routesByPath,
        legacyScopesOfCurrentUser,
      );
      // skip menu items with existing children that ended up empty after filtering
      if (newChildren?.length === 0) {
        return acc;
      }
    }
    if (isRestrictedMenuTreeNode(item)) {
      return acc;
    }
    acc.push({
      ...item,
      children: newChildren,
    });
    return acc;
  }, []);
};

/**
 * Hide menu items gated by a disabled feature flag, and neutralise unlicensed
 * banner items.
 *
 * Kept separate from permission filtering because feature flags are NOT permissions:
 * this must apply to every user (admins included), whereas permission filtering is
 * short-circuited for admins.
 *
 * A banner item advertises an unlicensed feature; like its (permission-free) banner
 * route, it must NOT inherit the real feature's `apiScopeRequirement`. We strip it
 * here so the later permission filter keeps the item visible to its target audience
 * (non-admin users without the feature license). Requires this filter to run BEFORE
 * the permission filter — see {@link filterMenuTree}. NIM-23306.
 */
export const filterMenuTreeByPageRequirements = (
  menuTreeNodes: MenuTreeNode[],
  licenseFeatureAvailability: LicenseFeatureAvailability,
): MenuTreeNode[] =>
  menuTreeNodes.reduce<MenuTreeNode[]>((acc, item) => {
    const accessRule = pageAccessRuleFromOasNode(item);
    const access = evaluatePageAccess(accessRule, licenseFeatureAvailability);
    if (access === 'hidden') {
      return acc;
    }

    let newChildren: MenuTreeNode[] | undefined;
    if (item.children) {
      newChildren = filterMenuTreeByPageRequirements(
        item.children,
        licenseFeatureAvailability,
      );
      // skip parents whose children were all filtered out
      if (newChildren.length === 0) {
        return acc;
      }
    }

    acc.push({
      ...item,
      children: newChildren,
      ...(access === 'unlicensed' ? { apiScopeRequirement: undefined } : {}),
    });
    return acc;
  }, []);

/**
 * Hide the License overview page unless the current snapshot supports the new cloud licensing
 * model.
 *
 * Unlike feature flags and license features, this depends on the selected snapshot, so it cannot
 * be expressed as a page access rule evaluated on the OAS node. NIM-24746.
 */
export const filterMenuTreeBySnapshotLicensing = (
  menuTreeNodes: MenuTreeNode[],
  cloudLicensingSupported: boolean | undefined,
): MenuTreeNode[] => {
  if (cloudLicensingSupported) {
    return menuTreeNodes;
  }

  return menuTreeNodes.reduce<MenuTreeNode[]>((acc, item) => {
    if (item.urlList?.[0] === CLOUD_LICENSING_PAGE_PATH) {
      return acc;
    }

    let newChildren: MenuTreeNode[] | undefined;
    if (item.children) {
      newChildren = filterMenuTreeBySnapshotLicensing(
        item.children,
        cloudLicensingSupported,
      );
      // skip parents whose children were all filtered out
      if (newChildren.length === 0) {
        return acc;
      }
    }

    acc.push({
      ...item,
      children: newChildren,
    });
    return acc;
  }, []);
};

/**
 * Apply both menu filters in the order required for teasers to work:
 * feature-flag filter first (drops disabled items, neutralises teasers), then
 * permission filter. Reordering these would re-break the teaser visibility (see
 * {@link filterMenuTreeByPageRequirements}). NIM-23306.
 */
export const filterMenuTree = (
  menuTreeNodes: MenuTreeNode[],
  meetsApiScope: MeetsApiScopeFunction,
  routesByPath: RoutesByPath,
  legacyScopesOfCurrentUser: PermissionScope[],
  licenseFeatureAvailability: LicenseFeatureAvailability,
  admin?: boolean,
): MenuTreeNode[] =>
  filterMenuTreeByPermissionRestrictions(
    filterMenuTreeByPageRequirements(menuTreeNodes, licenseFeatureAvailability),
    meetsApiScope,
    routesByPath,
    legacyScopesOfCurrentUser,
    admin,
  );

export const selectMenuTree: (s: GlobalStateMergedState) => MenuTreeNode[] =
  createScopedSelector(() =>
    createSelector(
      selectWholeMenuTree,
      selectMeetsApiScope,
      selectAllRoutesByPath,
      prop('auth', 'scope'),
      prop('auth', 'admin'),
      selectLicenseFeatureAvailability,
      prop('cloudLicensingSupported'),
      (
        menuTreeNodes,
        meetsApiScope,
        routesByPath,
        scope,
        admin,
        licenseFeatureAvailability,
        cloudLicensingSupported,
      ) =>
        filterMenuTreeBySnapshotLicensing(
          filterMenuTree(
            menuTreeNodes,
            meetsApiScope,
            routesByPath,
            scope,
            licenseFeatureAvailability,
            admin,
          ),
          cloudLicensingSupported,
        ),
    ),
  );
